/**
 * Shown in place of a page when the account check failed before the server
 * confirmed a role (`accountStateOf` → 'failed'). Every role can land here,
 * students and parents included, so the copy names no role.
 *
 * Without this screen the guards would read the provider's starting defaults
 * (a student) and route a teacher to the student home. Try again reruns the
 * check; until it succeeds the screen stays put, never flashing those defaults.
 */
import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

interface AccountLoadErrorProps {
  onRetry: () => Promise<void>;
}

function AccountLoadError({ onRetry }: AccountLoadErrorProps) {
  const [retrying, setRetrying] = useState(false);

  const retry = async () => {
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: 420 }}>
        <Stack role="alert" spacing={1} alignItems="center">
          <Typography variant="h5" component="h1" fontWeight={700}>
            Couldn’t load your account
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Check your connection, then try again.
          </Typography>
        </Stack>
        <Button
          variant="contained"
          color="primary"
          disabled={retrying}
          aria-busy={retrying || undefined}
          onClick={() => void retry()}
        >
          Try again
        </Button>
      </Stack>
    </Box>
  );
}

export default AccountLoadError;
